import { Eフェーズ種別, E入力分類, E科目別計画設定番号 } from '@/openapi/enum';
import { データListItemResponse } from '@/openapi/user';

import 決算期Utility, { 決算年 } from '@/classes/common/決算期Utility';
import { PlbscfTypeList, PlbscfYearValue, YearItemType } from '@/classes/service/AbstractPlbscfService';
import AbstractデータService from '@/classes/service/AbstractデータService';
import Abstract計画PlbscfService, { 業績予測 } from '@/classes/service/Abstract計画PlbscfService';
import 計画PlbscfService from '@/classes/service/計画/計画PlbscfService';

// ------------------------------------------------------------------
// モニタリング共通
// ------------------------------------------------------------------
export default abstract class AbstractMonitoringService<T> extends Abstract計画PlbscfService<T> {
  // ------------------------------------------------------------------
  // fields
  // ------------------------------------------------------------------
  protected 計画PlbscfService: 計画PlbscfService | null = null;

  protected readonlyMonthIndex: number | null = null;

  protected 会計期間Year = 0;

  // ------------------------------------------------------------------
  // static methods
  // ------------------------------------------------------------------

  /**
   * 入力不可としたい monthlyValues の index を取得する
   * ・0 ～ index まで入力不可
   * ・nullの場合は全てのindexが入力可
   *
   * @returns
   */
  public static getReadonlyMonthIndex(): number | null {
    const 期数 = 決算期Utility.diffYearFrom0期(this.getモニタリング会計期間Year());
    const 年月s = 決算期Utility.get計画期年月s(期数);
    const getYm = (ym: { year: number; month: number }): number => {
      return ym.year * 100 + ym.month;
    };
    const c実績直近月 = AbstractデータService.計画書.c実績直近月;
    const lastIndex = 年月s.length - 1;

    // c実績直近月 < 最初の月　の場合、全ての月は見込なので入力可
    if (c実績直近月 < getYm(年月s[0])) return null;
    // c実績直近月 >= 最後の月　の場合、全ての月は実績なので入力不可
    if (c実績直近月 >= getYm(年月s[lastIndex])) return lastIndex;

    // 最初の月 <= c実績直近月 < 最後の月　の場合
    return 年月s.findIndex((ym) => c実績直近月 === getYm(ym));
  }

  /**
   * value1, 2から業績予測を計算
   *
   * @param value1
   * @param value2
   * @returns
   */
  protected static calculate業績予測(value1: number, value2: number): 業績予測 {
    const roundingMode = this.getBigNumberRoundingMode(this.事業所.c実績値及び計画値端数処理);
    return {
      差異: value1 - value2,
      比率: this.calcPercentByBigNumber(value1, 100, value2, roundingMode),
    };
  }

  /**
   * 「cモニタリング会計期間」の年のみ取得
   *
   * @returns
   */
  protected static getモニタリング会計期間Year(): number {
    const cモニタリング会計期間 = AbstractデータService.計画書.cモニタリング会計期間;
    return cモニタリング会計期間 === undefined
      ? 決算期Utility.get計画0期の決算年()
      : 決算期Utility.getYear(cモニタリング会計期間);
  }

  // ------------------------------------------------------------------
  // override protected methods
  // ------------------------------------------------------------------

  // @override
  protected isMonthlyByYear(_year: number): boolean {
    return true;
  }

  // @override
  protected isMonthlyBy決算年(_決算年: 決算年): boolean {
    return true;
  }

  // @override
  protected createMonitoringForPlbscf(
    typeList: PlbscfTypeList | undefined,
    i: number
  ): { yearValue: PlbscfYearValue } | undefined {
    const yv = typeList!.list[i].yearValues.find((yv) => yv.year === this.会計期間Year)!;
    return {
      yearValue: {
        ...yv,
        type: YearItemType.readonly,
      },
    };
  }

  // ------------------------------------------------------------------
  // protected methods
  // ------------------------------------------------------------------

  /**
   * 初期処理
   *
   * @param データs
   * @param option
   * @returns
   */
  protected initializeService(
    データs: データListItemResponse[],
    option?: Record<string, string | number | AbstractデータService<unknown>>
  ): void {
    // 初期化
    this.initializeProperty();

    // 計画PlbscfServiceから値をコピー
    this.copyBy計画PlbscfService(データs, option);

    // 決算期・ヘッダーを準備しておく
    this.create決算期Headers();

    // モニタリングのtデータを設定（PLBSCF、科目別計画）
    this.setRawTデータ(データs);
  }

  /**
   * 初期化
   *
   * @returns
   */
  protected initializeProperty(): void {
    this.readonlyMonthIndex = AbstractMonitoringService.getReadonlyMonthIndex();
    this.会計期間Year = AbstractMonitoringService.getモニタリング会計期間Year();
  }

  /**
   * 計画PlbscfServiceから値をコピーする
   *
   * @param データs
   * @param option
   * @returns
   */
  protected copyBy計画PlbscfService(
    データs: データListItemResponse[],
    option?: Record<string, string | number | AbstractデータService<unknown>>
  ): void {
    if (this.計画PlbscfService === null) {
      this.計画PlbscfService =
        option?.計画PlbscfService === undefined
          ? new 計画PlbscfService(Eフェーズ種別.計画フェーズ)
          : (option?.計画PlbscfService as 計画PlbscfService);
    }
    this.計画PlbscfService.set勘定科目s = this.勘定科目s;
    this.計画PlbscfService.set部門s = this.部門s;
    this.計画PlbscfService.parse(データs);

    const properties = this.計画PlbscfService.getPropertiesToCopyMonitoring;
    this.分析PlbscfService = properties.分析PlbscfService;
    this.償却Service = properties.償却Service;
    this.返済明細Service = properties.返済明細Service;
    this.償却 = properties.償却;
    this.分析PlbscfTypeLists = properties.分析PlbscfTypeLists;
  }

  /**
   * 決算期・ヘッダーを作成する
   *
   * @returns
   */
  protected create決算期Headers(): void {
    // 継承先で実装
  }

  /**
   * plbscfの計算用データに計算結果を格納
   *
   * @returns
   */
  protected calcPlbscfValues(): void {
    // 計算用データを作成
    this.setCalcTypeLists(this.計画PlbscfService?.getCalcTypeLists);
    // 損益合計算出／転記
    this.setTotal損益AndTranscribe(this.計画PlbscfService?.getCalcTypeLists);
    // 計算結果を設定
    this.setValuesByCalcResult();
  }

  /**
   * モニタリングのtデータを設定（PLBSCF、科目別計画）
   *
   * @param データs
   * @returns
   */
  private setRawTデータ(データs: データListItemResponse[]): void {
    const plbscfデータs = this.フェーズ種別Filter(this.入力分類Filter(データs, [E入力分類.モニタリングPLBSCF]));
    this.rawTデータPlbscfs = this.createTデータPlbscfs(plbscfデータs);
    this.tデータ科目別計画Configs = this.createTデータ科目別計画Configs(
      データs,
      E科目別計画設定番号.モニタリングフェーズ
    );
  }
}
